import type { GetPrintJobPayload, PrintJob, PrintPagesPayload, PrintPagesResult } from '@publicala/delfino';
import { hostBridge } from '@publicala/delfino';
import { createPrintJobMock, createPrintPagesMock, isE2EMockActive } from '../e2eMockGuard';

export type { GetPrintJobPayload, PrintJob, PrintPagesPayload, PrintPagesResult };

/**
 * Print API for Volpe. Wraps the host endpoint that generates the controlled,
 * watermarked PDF, following the same shape as SessionAPI/ContentAPI.
 */
class PrintAPI {
    /**
     * Requests a controlled print job for a page range. The host (farfalla)
     * generates the watermarked PDF and resolves with its id + temporary URL.
     *
     * @param payload - File type, page range and whether it is a new print or a reprint
     * @returns The generated print job
     */
    async requestPrintPages(payload: PrintPagesPayload): Promise<PrintPagesResult> {
        if (isE2EMockActive()) {
            return createPrintPagesMock(payload);
        }
        return hostBridge.print.requestPrintPages(payload);
    }

    /**
     * Reads where a print job stands. Only a host whose session advertises
     * `print.readerDelivery` answers it.
     *
     * @param payload - The id `requestPrintPages` resolved with
     * @returns The job: pending, ready with the URL that opens its PDF, or failed
     */
    async getPrintJob(payload: GetPrintJobPayload): Promise<PrintJob> {
        if (isE2EMockActive()) {
            return createPrintJobMock(payload);
        }
        return hostBridge.print.getPrintJob(payload);
    }
}

export const printAPI = new PrintAPI();
